import React, { useState, useEffect } from 'react'
import { User, LogOut, Image, Heart, Crown, Edit2, Save, X, Shield, Search, Users, Zap } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getUserImages, getUserFavorites } from '../../lib/supabase'
import PrivacySettingsModal from '../Privacy/PrivacySettings'
import UserSearch from '../Search/UserSearch'
import CreditBalance from '../Credits/CreditBalance'
import CreditHistory from '../Credits/CreditHistory'

interface UserProfileProps {
  isOpen: boolean
  onClose: () => void
}

const UserProfile: React.FC<UserProfileProps> = ({ isOpen, onClose }) => {
  const { user, profile, signOut, updateProfile } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    display_name: '',
    username: '',
    bio: ''
  })
  const [userImages, setUserImages] = useState<any[]>([])
  const [userFavorites, setUserFavorites] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<'images' | 'favorites' | 'social' | 'privacy' | 'credits'>('images')
  const [isLoading, setIsLoading] = useState(false)

  // Modal states
  const [showPrivacySettings, setShowPrivacySettings] = useState(false)
  const [showUserSearch, setShowUserSearch] = useState(false)

  useEffect(() => {
    if (profile) {
      setEditForm({
        display_name: profile.display_name || '',
        username: profile.username || '',
        bio: profile.bio || ''
      })
    }
  }, [profile])

  useEffect(() => {
    if (isOpen && user) {
      loadUserData()
    }
  }, [isOpen, user])

  const loadUserData = async () => {
    if (!user) return

    try {
      setIsLoading(true)
      const [images, favorites] = await Promise.all([
        getUserImages(user.id),
        getUserFavorites(user.id)
      ])
      
      setUserImages(images || [])
      setUserFavorites(favorites || [])
    } catch (error) {
      console.error('Error loading user data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveProfile = async () => {
    if (!user) return
    
    try {
      await updateProfile(editForm)
      setIsEditing(false)
    } catch (error) {
      console.error('Error updating profile:', error)
    }
  }

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'premium':
        return <Crown className="w-5 h-5 text-yellow-500" />
      case 'pro':
        return <Crown className="w-5 h-5 text-purple-500" />
      default:
        return null
    }
  }

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'premium':
        return 'bg-yellow-100 text-yellow-800'
      case 'pro':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isOpen || !user || !profile) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Profile</h2>
            <button 
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Profile Info */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>

              <div className="flex-1">
                {isEditing ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editForm.display_name}
                      onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Display Name"
                    />
                    <input
                      type="text"
                      value={editForm.username}
                      onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Username"
                    />
                    <textarea
                      value={editForm.bio}
                      onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Tell us about yourself..."
                      rows={3}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveProfile}
                        className="flex items-center space-x-1 px-3 py-1 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="flex items-center space-x-1 px-3 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {profile.display_name || profile.username || 'Anonymous User'}
                      </h3>
                      {getTierIcon(profile.subscription_tier)}
                    </div>
                    
                    {profile.username && (
                      <p className="text-sm text-gray-500 mb-2">@{profile.username}</p>
                    )}
                    
                    <div className="flex items-center space-x-2 mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadge(profile.subscription_tier)}`}>
                        {profile.subscription_tier.charAt(0).toUpperCase() + profile.subscription_tier.slice(1)}
                      </span>
                      <span className="text-sm text-gray-600">
                        {profile.total_images_generated} images generated
                      </span>
                    </div>

                    {profile.bio && (
                      <p className="text-gray-700 mb-3">{profile.bio}</p>
                    )}
                    
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span>Edit Profile</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('images')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium ${
                activeTab === 'images'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <Image className="w-4 h-4" />
                <span>Images ({userImages.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('favorites')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium ${
                activeTab === 'favorites'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <Heart className="w-4 h-4" />
                <span>Favorites ({userFavorites.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('social')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium ${
                activeTab === 'social'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <Users className="w-4 h-4" />
                <span>Social</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium ${
                activeTab === 'privacy'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <Shield className="w-4 h-4" />
                <span>Privacy</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`flex-shrink-0 py-3 px-4 text-center font-medium ${
                activeTab === 'credits'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <Zap className="w-4 h-4" />
                <span>Credits</span>
              </div>
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                {activeTab === 'images' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {userImages.length > 0 ? (
                      userImages.map((image: any) => (
                        <div key={image.id} className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                          <img
                            src={image.image_url}
                            alt={image.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-8 text-gray-500">
                        <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No images generated yet</p>
                        <p className="text-sm">Start creating to see your images here!</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'favorites' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {userFavorites.length > 0 ? (
                      userFavorites.map((favorite: any) => (
                        <div key={favorite.id} className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                          <img
                            src={favorite.images.image_url}
                            alt={favorite.images.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-8 text-gray-500">
                        <Heart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No favorites yet</p>
                        <p className="text-sm">Heart images you love to save them here!</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'social' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Users className="w-5 h-5 mr-2" />
                        Connect with Others
                      </h3>
                      
                      <div className="grid gap-4">
                        <button
                          onClick={() => setShowUserSearch(true)}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Search className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="text-left">
                              <h4 className="font-medium text-gray-900">Find People</h4>
                              <p className="text-sm text-gray-500">Search for users by email, phone, or username</p>
                            </div>
                          </div>
                          <div className="text-gray-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </button>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <Users className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">Your Network</h4>
                              <p className="text-sm text-gray-500">Manage your connections and friend requests</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <div className="text-lg font-semibold text-gray-900">0</div>
                              <div className="text-xs text-gray-500">Friends</div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <div className="text-lg font-semibold text-gray-900">0</div>
                              <div className="text-xs text-gray-500">Pending</div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <div className="text-lg font-semibold text-gray-900">0</div>
                              <div className="text-xs text-gray-500">Blocked</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'privacy' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Shield className="w-5 h-5 mr-2" />
                        Privacy & Security
                      </h3>
                      
                      <div className="grid gap-4">
                        <button
                          onClick={() => setShowPrivacySettings(true)}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Shield className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="text-left">
                              <h4 className="font-medium text-gray-900">Privacy Settings</h4>
                              <p className="text-sm text-gray-500">Control who can see your profile and content</p>
                            </div>
                          </div>
                          <div className="text-gray-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </button>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                              <Shield className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">Account Security</h4>
                              <p className="text-sm text-gray-500">Your account security status</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Email Verified</span>
                              <span className="text-green-600 font-medium">✓ Yes</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Phone Verified</span>
                              <span className="text-gray-400">Not set</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Profile Visibility</span>
                              <span className="text-blue-600 font-medium">Public</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'credits' && (
                  <div className="space-y-6">
                    <CreditBalance size="lg" showPurchaseButton={true} />
                    <CreditHistory limit={10} showFilters={true} />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={signOut}
              className="w-full flex items-center justify-center space-x-2 py-2 px-4 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Privacy Settings Modal */}
      {showPrivacySettings && (
        <PrivacySettingsModal onClose={() => setShowPrivacySettings(false)} />
      )}

      {/* User Search Modal */}
      {showUserSearch && (
        <UserSearch onClose={() => setShowUserSearch(false)} />
      )}
    </>
  )
}

export default UserProfile 